import type { AxiosError } from "axios";
import jwtDecode from "jwt-decode";

/**
 * Why refreshing the access token failed. It decides what the user experiences:
 *
 *  - "session-over":  the API says the session has ended (no refresh cookie, unknown user, refresh
 *                     token expired or revoked). The user is logged out.
 *  - "unreachable":   no answer, a timeout, or the gateway reporting the API down or busy (e.g. while
 *                     a new version is being deployed). The user stays where they are; the next
 *                     request tries again.
 *  - "server-error":  the API answered with an unexpected error. Treated like "unreachable" at first,
 *                     but if it keeps happening the user is logged out, so nobody is left on a page
 *                     where nothing works.
 */
export type RefreshFailure = "session-over" | "unreachable" | "server-error";

/**
 * What GET /api/user/refresh-token answers, with HTTP 400, when the session is definitively over.
 * Must stay in step with UserController.Refresh in the API.
 */
export const SESSION_OVER_REASONS = [
	"refresh-token-claim-not-found",
	"user-not-found",
	"refresh-token-revoked",
];

/** Statuses meaning the API could not be reached or is overloaded, rather than that it refused. */
const UNREACHABLE_STATUSES = [408, 429, 502, 503, 504];

export function classifyRefreshFailure(err: unknown): RefreshFailure {
	const response = (err as AxiosError | null | undefined)?.response;
	if (!response) {
		return "unreachable"; // network error, timeout, CORS failure, or an answer that is not a token
	}
	if (response.status === 401) {
		return "session-over";
	}
	if (
		response.status === 400 &&
		SESSION_OVER_REASONS.includes(reasonOf(response.data))
	) {
		return "session-over";
	}
	if (UNREACHABLE_STATUSES.includes(response.status)) {
		return "unreachable";
	}
	return "server-error";
}

/** The plain-text reason of a 400, whether it arrived bare or as a JSON string. */
function reasonOf(data: unknown): string {
	return typeof data === "string"
		? data.trim().replace(/^"(.*)"$/, "$1")
		: "";
}

/**
 * True when the JWT expires within `seconds` from now, or already has. False when the token cannot
 * be read: the API rejects such a token and the 401 path takes over.
 *
 * Uses jwt-decode rather than atob(): a JWT is base64url-encoded, and atob() throws on the "-" and
 * "_" characters that encoding produces, which silently disabled the proactive refresh.
 */
export function isTokenExpiringWithin(
	token: string,
	seconds: number,
	now: number = Date.now()
): boolean {
	try {
		const exp = Number(jwtDecode<{ exp?: number | string }>(token).exp);
		return Number.isFinite(exp) && exp * 1000 - now < seconds * 1000;
	} catch {
		return false;
	}
}

/**
 * True for a string shaped like a JWT. Keeps a 200 OK that is not the API's answer (a captive portal
 * or proxy page) from being stored as the access token.
 */
export function looksLikeJwt(value: unknown): value is string {
	return typeof value === "string" && /^[\w-]+\.[\w-]+\.[\w-]+$/.test(value);
}

export const sleep = (ms: number) =>
	new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface SessionRefresherOptions {
	/** Performs ONE refresh request and resolves with the new access token. */
	request: () => Promise<string>;
	/** Called once per successful refresh, with the new access token. */
	onRefreshed: (token: string) => void;
	/** Called once when a refresh failure ends the session. */
	onSessionEnded: () => void;
	/** Pause before each retry of a failure that may pass; one retry per entry. */
	retryDelaysMs?: number[];
	/** How long the API may keep answering with unexpected errors before the session is ended anyway. */
	serverErrorGraceMs?: number;
	now?: () => number;
	sleep?: (ms: number) => Promise<void>;
}

/**
 * Refreshes the access token, and decides when a failure ends the session.
 *
 * Before this, ANY failed refresh logged the user out, so a dropped connection or a deploy restarting
 * the API in the minute their 5-minute token ran out threw them back to the login page mid-work.
 * Now only the API saying the session is over does that at once; anything that may pass is retried,
 * and if it still fails the user stays on the page and their next action tries again.
 */
export function createSessionRefresher({
	request,
	onRefreshed,
	onSessionEnded,
	retryDelaysMs = [1000, 3000],
	serverErrorGraceMs = 60 * 1000,
	now = () => Date.now(),
	sleep: pause = sleep,
}: SessionRefresherOptions) {
	let inFlight: Promise<string> | null = null;
	// When the API first answered a refresh with an unexpected error since the last success.
	let failingSince: number | null = null;
	const sessionEnders = new WeakSet<object>();

	async function requestWithRetries(): Promise<string> {
		for (let attempt = 0; ; attempt++) {
			try {
				return await request();
			} catch (err) {
				if (
					classifyRefreshFailure(err) === "session-over" ||
					attempt >= retryDelaysMs.length
				) {
					throw err;
				}
				await pause(retryDelaysMs[attempt]);
			}
		}
	}

	async function run(): Promise<string> {
		try {
			const token = await requestWithRetries();
			failingSince = null;
			onRefreshed(token);
			return token;
		} catch (err) {
			const failure = classifyRefreshFailure(err);
			let ended = failure === "session-over";
			if (failure === "server-error") {
				if (failingSince === null) {
					failingSince = now();
				}
				ended = now() - failingSince >= serverErrorGraceMs;
			}
			if (ended) {
				failingSince = null;
				if (typeof err === "object" && err !== null) {
					sessionEnders.add(err);
				}
				onSessionEnded();
			}
			throw err;
		}
	}

	return {
		/** Refreshes the access token; concurrent callers share one refresh. Rejects with the last error. */
		refresh(): Promise<string> {
			if (!inFlight) {
				inFlight = run().finally(() => {
					inFlight = null;
				});
			}
			return inFlight;
		},
		/** True when `err` is a refresh failure that ended the session (the user has been logged out). */
		endedSession(err: unknown): boolean {
			return (
				typeof err === "object" &&
				err !== null &&
				sessionEnders.has(err)
			);
		},
	};
}
